// @TODO The Swipable will be covered as part of the https://autovia.atlassian.net/browse/AVM-1076

/**
 * @render react
 * @name Gallery
*/

import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { compose } from 'recompose';
import { Swipeable } from 'react-swipeable';

import { withAmp } from 'Components/protons/withAmp';
import { withPolaris } from 'Components/protons/Polaris';

import { Button } from 'Components/atoms/Button';
import { Image } from 'Components/atoms/Image';
import { Link } from 'Components/molecules/Link';
import { Video } from 'Components/molecules/Video';

import renderAmp from './amp';

import './_style.scss';

/**
 * Gallery component
 *
 * @param {string} cover - The initial cover image for the gallery. Upon clicking the cover, the gallery will open
 * @param {boolean} mediaLazyLoad - Defines whether the media need to be lazyloaded or not
 * @param {number} index - The image index we want to show by default when opening the gallery
 * @param {string} mode - The mode we want. Can either be open,close, fullscreen, swipe, list
 * @param {string} url - URL for the linkout mode
 * @param {array} media - Array of media with objects with the thumb and large keys
 * @param {string} viewAllUrl - URL for for View All link
 * @param {string} viewAllLabel - Label for View All link
 * @param {boolean} loadStyles - Whether to apply the class names for styling
 * @param {Object} extraClassNames - Extra class names to be applied to the component
 * @param {Object} context - Extra information coming from Polaris provider.
 * @param {string} badge - object containing src (string) to the icon background image and alt text (string)
 */
class Gallery extends PureComponent {
  /**
  * Constructor
  */
  constructor(props) {
    super(props);
    const {
      media, mode, index, totalMedia,
    } = this.props;

    // Check for valid index
    let newIndex = index;
    if (index < 0 || index > (media.length - 1)) {
      newIndex = 0;
    }

    // Set the initial state
    this.state = {
      index: 0 || newIndex, // starting image for the gallery
      mode: mode || 'close', // can be open,close,fullscreen, swipe, linkout
      totalMedia: totalMedia || media.length, // so that we don't calculate the length each time
    };

    this.openGallery = this.openGallery.bind(this);
    this.showNextImage = this.showNextImage.bind(this);
    this.showPreviousImage = this.showPreviousImage.bind(this);
    this.close = this.close.bind(this);
    this.viewFullscreen = this.viewFullscreen.bind(this);
  }

  /**
   * Returns the correct Component depending on the media provided
   * @param {object} contentMedia
   * @param index
   */
  getContent(contentMedia, index) {
    const {
      mode,
      mediaLazyLoad,
      context: { cssPrefix },
    } = this.props;
    let lazyLoadEnabled = mediaLazyLoad;
    let isLCPCandidate = false;

    if (!contentMedia) {
      return false;
    }

    if (contentMedia.__typename === 'Video') {
      if (contentMedia.url || contentMedia.src) {
        return (
          <>
            <Video
              url={contentMedia.url || contentMedia.src}
              playing={false}
            />
            {contentMedia.caption && (
              <div className={`${cssPrefix}__gallery--caption`}>{contentMedia.caption}</div>
            )}
          </>
        );
      }
      // details about media data type video is yet to be defined
      // @TODO temp
      return (
        <div data-type={contentMedia.__typename} />
      );
    }

    if (contentMedia.__typename === 'LinkedMedia') {
      return null;
    }

    if (contentMedia.__typename === 'Advertisement') {
      return contentMedia.component;
    }

    // Disabling lazyLoad and set isLCPCandidate in case the Gallery mode is list
    // And the item is the first of the list
    if (mode === 'list' && index === 0) {
      lazyLoadEnabled = false;
      isLCPCandidate = true;
    }

    return (
      <Image
        src={contentMedia.large}
        alt={contentMedia.alt}
        srcSet={{ small: contentMedia.large, medium: contentMedia.large, large: contentMedia.large }}
        width={contentMedia.width || '100%'}
        height={contentMedia.height || 'auto'}
        extraClassNames={{ '-pointer-events-none': true }}
        caption={contentMedia.caption}
        credits={contentMedia.credits}
        displayImageMetadata
        size="content-image-full"
        lazyLoad={lazyLoadEnabled}
        isLCPCandidate={isLCPCandidate}
      />
    );
  }

  /**
   * Closes the gallery by switching the mode to close
   */
  close() {
    const { mode } = this.state;
    let newMode = 'close';
    if (mode === 'fullscreen') {
      newMode = 'open';
    }

    this.setState({
      mode: newMode,
    });
  }

  /**
   * Switches to fullscreen mode
   */
  viewFullscreen() {
    this.setState({
      mode: 'fullscreen',
    });
  }

  /**
   * Goes to a specific index
   * @param {number} index - Index where we want to show the image
   */
  goToIndex(index) {
    this.setState({
      index,
    });
  }

  /**
   * Shows the previous image in the gallery
   */
  showPreviousImage() {
    const { index, totalMedia } = this.state;
    let newIndex = index;

    if (index === 0) {
      newIndex = (totalMedia - 1);
    } else {
      newIndex = (index - 1);
    }

    this.setState({
      index: newIndex,
    });
  }

  /**
   * Shows the next image in the gallery
   */
  showNextImage() {
    const { index, totalMedia } = this.state;
    let newIndex = index;

    if (index === (totalMedia - 1)) {
      newIndex = 0;
    } else {
      newIndex = (index + 1);
    }

    this.setState({
      index: newIndex,
    });
  }

  /**
   * Opens the gallery by switching the mode to open
   */
  openGallery() {
    this.setState({
      mode: 'open',
    });
  }

  /**
   * Renders the amp version of the component.
   */
  amp() {
    return renderAmp.bind(this)();
  }

  /**
   * Render our component
   */
  render() {
    const {
      cover,
      mediaLazyLoad,
      url,
      viewAllUrl,
      viewAllLabel,
      extraClassNames,
      media,
      displayArrows,
      badge,
      loadStyles,
      context: { cssPrefix },
    } = this.props;

    if (media.length === 0) return null;

    const {
      index,
      mode,
      totalMedia,
    } = this.state;

    return (
      <div
        className={classNames({
          [`${cssPrefix}__gallery`]: loadStyles,
          [`-${mode}`]: true,
          '-has-badge': badge && badge.src,
          ...extraClassNames,
        })}
      >
        { badge && badge.src !== '' && (
          <Image
            {...badge}
            extraClassNames={{ [`${cssPrefix}__gallery--badge`]: true }}
          />
        )}

        { mode === 'linkout' && (
          <div className={`${cssPrefix}__gallery--cover`}>
            {cover && cover.src && (
              <Link href={url}>
                <Image
                  size="content-image-full"
                  {...cover}
                  lazyLoad={mediaLazyLoad}
                />
                <span className={`${cssPrefix}__gallery--slide-info`}>
                  {totalMedia}
                </span>
              </Link>
            )}
          </div>
        )}

        { mode === 'list' && (
          <ul className={`${cssPrefix}__gallery--list`}>
            {media.map((content, i) => (
              <li
                key={i}
                className={classNames({
                  '-linked-media': content.__typename === 'LinkedMedia',
                  '-video': content.__typename === 'Video',
                  '-image': content.__typename === 'Image',
                  '-ad': content.__typename === 'Advertisement',
                })}
              >
                { this.getContent(content, i) }
              </li>
            ))}
          </ul>
        )}

        {
          mode === 'swipe'
          && (
            <div className={`${cssPrefix}__gallery--cover`}>
              <div className={`${cssPrefix}__gallery--main`}>
                <Swipeable
                  onSwipedLeft={this.showNextImage}
                  onSwipedRight={this.showPreviousImage}
                  trackMouse
                >
                  { this.getContent(media[index]) }
                </Swipeable>

                {displayArrows && (
                  <div className={`${cssPrefix}__gallery--navigation`}>
                    <Button label="Previous" onClick={this.showPreviousImage} extraClassNames={{ [`${cssPrefix}__gallery--previous`]: true }} />
                    <Button label="Next" onClick={this.showNextImage} extraClassNames={{ [`${cssPrefix}__gallery--next`]: true }} />
                  </div>
                )}
              </div>

              <span className={`${cssPrefix}__gallery--slide-info`}>
                {`${index + 1}/${totalMedia}`}
                {viewAllUrl && <Link href={viewAllUrl}>{viewAllLabel}</Link>}
              </span>
            </div>
          )
        }
      </div>
    );
  }
}

/**
 * Valid props
 */
Gallery.propTypes = {
  cover: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    size: PropTypes.string,
  }),
  mediaLazyLoad: PropTypes.bool,
  index: PropTypes.number,
  mode: PropTypes.oneOf(['open', 'close', 'fullscreen', 'list', 'swipe', 'linkout']),
  url: PropTypes.string,
  viewAllUrl: PropTypes.string,
  viewAllLabel: PropTypes.string,
  media: PropTypes.arrayOf(PropTypes.shape({
    thumb: PropTypes.string,
    large: PropTypes.string,
    video: PropTypes.string,
    caption: PropTypes.string,
    credits: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  })),
  totalMedia: PropTypes.number,
  displayArrows: PropTypes.bool,
  loadStyles: PropTypes.bool,
  extraClassNames: PropTypes.shape(),
  context: PropTypes.shape(),
  ampScripts: PropTypes.shape(),
  badge: PropTypes.shape({
    src: PropTypes.string,
    alt: PropTypes.string,
    height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }),
};

/**
 * Default props
 */
Gallery.defaultProps = {
  cover: null,
  mediaLazyLoad: true,
  index: 0,
  mode: 'swipe',
  url: '',
  viewAllUrl: '',
  viewAllLabel: 'View All',
  media: [],
  totalMedia: null,
  displayArrows: true,
  loadStyles: true,
  extraClassNames: {},
  context: {},
  ampScripts: {
    'amp-carousel': 'https://cdn.ampproject.org/v0/amp-carousel-0.1.js',
  },
  badge: {
    src: '',
    alt: '',
    height: 0,
    width: 0,
  },
};

export default compose(withPolaris, withAmp)(Gallery);
